// Auto-assignment suggestion for the attacker's damage split (codex
// "Strike": the attacker divides its power among the defenders).
//
// Strategy: KILLS FIRST. Pick the set of defenders that can actually
// be destroyed with the available power. Most kills wins; among equal
// kill counts, destroy the biggest total power. A split that dumps
// everything on a defender it can't kill is strictly worse than one
// that kills something smaller: minion damage heals at the end of the
// turn, so non-lethal damage is cosmetic.
//
// Leftover power (every point must be assigned before striking) lands
// on the toughest survivor; if everything dies, it overkills the
// toughest victim.
//
// Pure. No React, no host. Defender counts are tiny (board-bounded),
// so exhaustive subset search is exact and instant.

/**
 * @param {number} power      attacker's power to distribute
 * @param {Array<{id: string, def: number|null}>} defenders
 *        A null `def` is an UNKILLABLE defender (a defending avatar, whose
 *        assigned damage is life loss): never worth "killing", always the
 *        best sink for leftover power.
 * @returns {Object<string, number>} id -> assigned damage (sums to power)
 */
export function autoAssignDamage(power, defenders) {
  const out = {};
  for (const d of defenders) out[d.id] = 0;
  const n = defenders.length;
  if (!n || !(power > 0)) return out;

  const cost = defenders.map((d) => (d.def == null ? Infinity : Math.max(0, Number(d.def) || 0)));
  let best = { mask: 0, count: 0, killedDef: 0 };
  for (let mask = 1; mask < (1 << n); mask++) {
    let sum = 0;
    let count = 0;
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) { sum += cost[i]; count++; }
    }
    if (sum > power) continue;
    if (count > best.count || (count === best.count && sum > best.killedDef)) {
      best = { mask, count, killedDef: sum };
    }
  }

  for (let i = 0; i < n; i++) {
    if (best.mask & (1 << i)) out[defenders[i].id] = cost[i];
  }
  const left = power - best.killedDef;
  if (left > 0) {
    const idx = defenders.map((_, i) => i);
    const survivors = idx.filter((i) => !(best.mask & (1 << i)));
    const pool = survivors.length ? survivors : idx;
    const dump = pool.reduce((a, b) => (cost[b] > cost[a] ? b : a));
    out[defenders[dump].id] += left;
  }
  return out;
}
